
export interface SchemaConfig {
  pageType: 'homepage' | 'blog-post' | 'blog-listing' | 'service' | 'contact' | 'about' | 'general';
  title?: string;
  description?: string;
  url: string;
  author?: string;
  publishedDate?: string;
  modifiedDate?: string;
  featuredImage?: string;
  excerpt?: string;
}

const organizationSchema = {
  "@type": "Organization",
  "@id": "https://www.orizis.co.uk/#organization",
  "name": "Orizis",
  "url": "https://www.orizis.co.uk",
  "logo": "https://www.orizis.co.uk/logo.png",
  "description": "UK's fastest-growing AI automation agency helping businesses streamline operations and boost efficiency",
  "address": {
    "@type": "PostalAddress",
    "addressCountry": "GB"
  },
  "sameAs": [
    "https://www.linkedin.com/company/orizistech/",
    "https://x.com/orizis"
  ]
};

export const generateSchema = (config: SchemaConfig) => {
  const { pageType, title, description, url, author, publishedDate, modifiedDate, featuredImage, excerpt } = config;

  switch (pageType) {
    case 'homepage':
      return {
        "@context": "https://schema.org",
        "@graph": [
          organizationSchema,
          {
            "@type": "WebSite",
            "@id": "https://www.orizis.co.uk/#website",
            "url": "https://www.orizis.co.uk",
            "name": "Orizis | AI Automation Agency",
            "description": "Transform your business with AI automation solutions. Orizis helps UK businesses streamline operations, reduce costs, and boost efficiency.",
            "publisher": {
              "@id": "https://www.orizis.co.uk/#organization"
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": {
                "@type": "EntryPoint",
                "urlTemplate": "https://www.orizis.co.uk/blog?search={search_term_string}"
              },
              "query-input": "required name=search_term_string"
            }
          }
        ]
      };

    case 'blog-post':
      return {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title,
        "description": excerpt || description,
        "author": {
          "@type": "Person",
          "name": author || "Orizis Team"
        },
        "publisher": organizationSchema,
        "datePublished": publishedDate,
        "dateModified": modifiedDate || publishedDate,
        "image": featuredImage,
        "url": url,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": url
        }
      };

    case 'blog-listing':
      return {
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Orizis Blog",
        "description": "Latest insights on AI automation, business efficiency, and digital transformation",
        "url": url,
        "publisher": organizationSchema
      };

    case 'service':
      return {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": title,
        "name": title,
        "description": description,
        "url": url,
        "provider": organizationSchema,
        "areaServed": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceRange": "Contact for pricing"
        }
      };

    case 'contact':
    case 'about':
      return {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        "name": "Orizis",
        "description": "AI automation agency specializing in business process optimization and digital transformation",
        "url": url,
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GB"
        },
        "serviceArea": {
          "@type": "Country",
          "name": "United Kingdom"
        },
        "knowsAbout": [
          "Artificial Intelligence",
          "Business Automation",
          "Process Optimization",
          "Digital Transformation"
        ]
      };

    default:
      return {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": title,
        "description": description,
        "url": url,
        "isPartOf": {
          "@type": "WebSite",
          "name": "Orizis",
          "url": "https://www.orizis.co.uk"
        },
        "publisher": organizationSchema
      };
  }
};
